<template>
    <div class="ActivityHistoryFiltersPanel">
        <div class="ActivityHistoryFiltersPanel__header">
            <span class="ActivityHistoryFiltersPanel__title">Filters</span>
            <NewButton
                class="ActivityHistoryFiltersPanel__clear"
                variant="flat"
                color="neutral"
                @click="clearFilters"
            >
                CLEAR
            </NewButton>
        </div>

        <div class="ActivityHistoryFiltersPanel__body">
            <DefaultLayout
                class="ActivityHistoryFiltersPanel__field"
                label="Event Type"
            >
                <NewDropdown
                    multi-select
                    :options="eventTypeOptions"
                    :selected="selectedEntryTypeOptions"
                    popup-content-class="ActivityHistoryFiltersPanel__dropdownPopup"
                    @input="onEntryTypeInput"
                />
            </DefaultLayout>
            <DefaultLayout
                v-if="showTags"
                class="ActivityHistoryFiltersPanel__field"
                label="Tag/Requirement"
            >
                <NewDropdown
                    multi-select
                    :options="tagOptions"
                    :selected="selectedTagOptions"
                    popup-content-class="ActivityHistoryFiltersPanel__dropdownPopup"
                    @input="onTagsInput"
                />
            </DefaultLayout>
            <TextInput
                id="activityHistoryFilterCausedBy"
                class="ActivityHistoryFiltersPanel__field"
                label="Event Source"
                :value="localFilters.causedBy"
                placeholder="SYSTEM, API, or email"
                @input="onCausedByInput"
            />
        </div>

        <div class="ActivityHistoryFiltersPanel__footer">
            <NewButton variant="flat" color="neutral" @click="$emit('close')">
                Cancel
            </NewButton>
            <NewButton color="primary" @click="applyFilters">
                Apply
            </NewButton>
        </div>
    </div>
</template>

<style lang="scss">
    .ActivityHistoryFiltersPanel {
        display: flex;
        flex-direction: column;
        width: 500px;
        background: var(--eidWhite);
        border: 1px solid var(--eidLightGray-25);
        border-radius: 8.75px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

        &__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px;
            border-bottom: 1px solid var(--eidLightGray-25);
        }

        &__title {
            font-weight: 600;
            font-size: 0.875rem;
        }

        &__body {
            flex: 1;
            padding: 16px;
            overflow-y: auto;
        }

        &__field {
            margin-bottom: 16px;

            &:last-child {
                margin-bottom: 0;
            }
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            gap: 8px;
            padding: 12px 16px;
            border-top: 1px solid var(--eidLightGray-25);
        }

        // Quasar teleports the dropdown to <body> — must exceed the Popover's z-index (100000)
        &__dropdownPopup {
            z-index: 100001;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { DropdownOption, NewDropdown } from '@evidentid/dashboard-commons/components/Dropdown';
    import { ActivityHistoryQuery, ActivityHistorySystemTag, ActivityHistorySystemTags, ActivityHistoryTagsMap, ActivityType, ClientTag, ENTITY_ACTIVITY_TYPES } from '@evidentid/tprm-portal-lib/models/activity-history';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { EnumCategories } from '@/modules/dashboard/models/EnumCategories.model';
    import DefaultLayout
        from '@/modules/dynamic-form/components/style-inputs/default-layout/DefaultLayout.vue';
    import TextInput
        from '@/modules/dynamic-form/components/style-inputs/text-input/TextInput.vue';
    import { ActivityHistoryFilters } from '@/modules/entity-events/models/ActivityHistoryFilters.model';

    export default defineComponent({
        name: 'ActivityHistoryFiltersPanel',
        components: { DefaultLayout, NewButton, NewDropdown, TextInput },
        props: {
            filters: {
                type: Object as PropType<ActivityHistoryFilters>,
                default: (): ActivityHistoryFilters => ({ entryType: [], tags: [], causedBy: '' }),
            },
            activityTypes: {
                type: Array as PropType<ActivityType[]>,
                default: (): ActivityType[] => ENTITY_ACTIVITY_TYPES,
            },
            enumLabels: {
                type: Object as PropType<CategorizedEnumLabels>,
                default: (): CategorizedEnumLabels => ({}) as CategorizedEnumLabels,
            },
            clientTags: {
                type: Array as PropType<string[]>,
                default: (): string[] => [] as string[],
            },
            systemTags: {
                type: Array as PropType<string[]>,
                default: (): string[] => [] as string[],
            },
            clientTagsMap: {
                type: Object as PropType<ActivityHistoryTagsMap | null>,
                default: null,
            },
            systemTagsMap: {
                type: Object as PropType<ActivityHistorySystemTags | null>,
                default: null,
            },
            showTags: {
                type: Boolean,
                default: true,
            },
        },
        emits: [ 'apply', 'close' ],
        data(): { localFilters: ActivityHistoryFilters } {
            return {
                localFilters: { ...this.filters },
            };
        },
        computed: {
            eventTypeOptions(): DropdownOption[] {
                const labels = (this.enumLabels as CategorizedEnumLabels)[EnumCategories.historyEntryType] || {};
                return (this.activityTypes as ActivityType[])
                    .map((type) => ({
                        value: type,
                        label: (labels as any)[type]?.label || type,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
            },
            selectedEntryTypeOptions(): DropdownOption[] {
                return this.localFilters.entryType.map(
                    (type) => this.eventTypeOptions.find((o) => o.value === type) || { value: type, label: type },
                );
            },
            tagOptions(): DropdownOption[] {
                const allSystemTags: ActivityHistorySystemTag[] = this.systemTagsMap
                    ? Object.values(this.systemTagsMap).flat()
                    : [];
                const systemTagLabelMap = new Map(allSystemTags.map((t) => [ t.key, t.label ]));
                const systemTagOptions = (this.systemTags as string[]).map((tag) => ({
                    value: tag,
                    label: systemTagLabelMap.get(tag) || tag,
                }));
                const allClientTags: ClientTag[] = this.clientTagsMap
                    ? (Object.values(this.clientTagsMap) as ClientTag[][]).flat()
                    : [];
                const clientTagLabelMap = new Map(allClientTags.map((t) => [ t.key, t.label ]));
                const clientTagOptions = (this.clientTags as string[]).map((tag) => ({
                    value: tag,
                    label: clientTagLabelMap.get(tag) || tag,
                }));
                return [ ...systemTagOptions, ...clientTagOptions ];
            },
            selectedTagOptions(): DropdownOption[] {
                return this.localFilters.tags.map(
                    (tag) => this.tagOptions.find((o) => o.value === tag) || { value: tag, label: tag },
                );
            },
        },
        watch: {
            filters(newFilters: ActivityHistoryFilters) {
                this.localFilters = { ...newFilters };
            },
        },
        methods: {
            onEntryTypeInput(options: DropdownOption[]): void {
                this.localFilters = { ...this.localFilters, entryType: options.map((o) => String(o.value)) };
            },
            onTagsInput(options: DropdownOption[]): void {
                this.localFilters = { ...this.localFilters, tags: options.map((o) => String(o.value)) };
            },
            onCausedByInput(event: Event): void {
                this.localFilters = { ...this.localFilters, causedBy: (event.target as HTMLInputElement).value };
            },
            applyFilters(): void {
                const query: ActivityHistoryQuery = {};
                if (this.localFilters.entryType.length) {
                    query.entryType = [ ...this.localFilters.entryType ];
                }
                if (this.localFilters.tags.length) {
                    query.tags = [ ...this.localFilters.tags ];
                }
                if (this.localFilters.causedBy.trim()) {
                    query.causedBy = this.localFilters.causedBy.trim();
                }
                this.$emit('apply', query);
            },
            clearFilters(): void {
                this.localFilters = { entryType: [], tags: [], causedBy: '' };
                this.$emit('apply', {});
            },
        },
    });
</script>
